import { useState, type FormEvent } from 'react'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { Clock, Plus, AlertCircle, Receipt } from 'lucide-react'
import { Button, Card, Chip, cn } from '../../design'
import { getStoryTimeSummary, logStoryTime } from '../../api/dkbooks'
import { ApiError } from '../../api/client'

/**
 * Story-drawer "Time" section — read-through to dkbooks.
 *
 *   Shows: total logged minutes, billed/unbilled split, vs estimate, recent entries
 *   Add: small form (duration in minutes + optional notes + optional date) → POST → dkbooks
 *
 * If dkbooks not configured / token missing — shows actionable hint.
 */
export function TimeLog({ storyId, storyKey }: { storyId: number; storyKey: string }) {
  const qc = useQueryClient()
  const [open, setOpen] = useState(false)
  const [duration, setDuration] = useState('30')
  const [notes, setNotes] = useState('')
  const [date, setDate] = useState('')
  const [error, setError] = useState<string | null>(null)

  const summaryQ = useQuery({
    queryKey: ['story-time', storyId],
    queryFn: () => getStoryTimeSummary(storyId),
    refetchOnWindowFocus: true,
    staleTime: 10_000,
    retry: false,
  })

  const logMut = useMutation({
    mutationFn: () =>
      logStoryTime(storyId, {
        duration_minutes: Math.max(1, Number(duration) || 0),
        notes: notes.trim() || undefined,
        entry_date: date || undefined,
      }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['story-time', storyId] })
      qc.invalidateQueries({ queryKey: ['dashboard'] })
      setNotes('')
      setDate('')
      setOpen(false)
      setError(null)
    },
    onError: (err) => {
      if (err instanceof ApiError) {
        setError(err.code === 'dkbooks_not_configured'
          ? 'No dkbooks token. Set one in Settings.'
          : err.code === 'not_configured'
            ? err.message
            : err.message)
      } else {
        setError('Failed to log time.')
      }
    },
  })

  function onSubmit(e: FormEvent) {
    e.preventDefault()
    setError(null)
    if (Number(duration) <= 0) { setError('Duration must be > 0 minutes.'); return }
    logMut.mutate()
  }

  // Render states ────────────────────────────────────────────────────────
  if (summaryQ.isLoading) {
    return (
      <Section>
        <p className="text-xs text-fg-subtle">Loading time…</p>
      </Section>
    )
  }
  if (summaryQ.error) {
    const err = summaryQ.error as Error & { code?: string }
    return (
      <Section>
        <div className="flex items-start gap-2 text-xs text-fg-muted">
          <AlertCircle size={14} className="text-warning shrink-0 mt-0.5" />
          <div>
            <div className="font-medium">dkbooks not connected</div>
            <div className="text-fg-subtle">
              {err.code === 'dkbooks_not_configured'
                ? <>Set your dkbooks API token in <a href="/settings/dkbooks" className="text-accent hover:underline">Settings → Integrations</a>.</>
                : (err.message ?? 'Unknown error')}
            </div>
          </div>
        </div>
      </Section>
    )
  }

  const s = summaryQ.data!
  const totalH = (s.total_minutes / 60).toFixed(1)
  const estMin = s.estimate_minutes
  const overEstimate = estMin != null && s.total_minutes > estMin

  return (
    <Section>
      {/* Stats */}
      <div className="grid grid-cols-3 gap-2 mb-3">
        <Stat label="Total" value={`${totalH}h`} sub={`${s.total_minutes}m`} />
        <Stat label="Billed" value={s.billed_minutes > 0 ? `${(s.billed_minutes / 60).toFixed(1)}h` : '—'} icon={<Receipt size={11} />} />
        <Stat
          label="Unbilled"
          value={s.unbilled_minutes > 0 ? `${(s.unbilled_minutes / 60).toFixed(1)}h` : '—'}
        />
      </div>

      {estMin !== null && estMin > 0 && (
        <div className="mb-3">
          <div className="flex items-center justify-between text-[10px] text-fg-subtle mb-1">
            <span>vs estimate</span>
            <span className="font-mono">{s.total_minutes}m / {estMin}m</span>
          </div>
          <div className="h-1.5 rounded-full bg-bg-muted overflow-hidden">
            <div
              className={cn('h-full transition-all', overEstimate ? 'bg-danger' : 'bg-accent')}
              style={{ width: `${Math.min(100, (s.total_minutes / estMin) * 100)}%` }}
            />
          </div>
        </div>
      )}

      {/* Recent entries */}
      {s.recent_entries.length > 0 && (
        <ul className="space-y-1 mb-3">
          {s.recent_entries.slice(0, 5).map(e => (
            <li key={e.id} className="flex items-center gap-2 text-xs px-2 py-1 rounded bg-bg-muted/50">
              <Clock size={11} className="text-fg-subtle shrink-0" />
              <span className="font-mono text-fg-muted shrink-0">{fmtMins(e.duration_minutes)}</span>
              <span className="text-fg-subtle shrink-0">{e.entry_date}</span>
              <span className="flex-1 truncate text-fg-muted">
                {(e.notes ?? '').replace(`[${storyKey}:`, '').replace(/\][^\]]*$/, '').replace(/\[[^\]]*\]$/, '').trim()}
              </span>
              {e.status === 'billed' && <Chip size="sm" tone="success">billed</Chip>}
            </li>
          ))}
        </ul>
      )}

      {!open && (
        <Button
          size="sm"
          variant="outline"
          leftIcon={<Plus size={12} />}
          onClick={() => setOpen(true)}
        >
          Log time
        </Button>
      )}

      {open && (
        <form onSubmit={onSubmit} className="rounded-md border border-border bg-bg p-3 space-y-2">
          <div className="grid grid-cols-2 gap-2">
            <label className="block">
              <span className="block text-[10px] uppercase tracking-wider text-fg-subtle font-medium mb-1">
                Duration (min)
              </span>
              <input
                type="number"
                min={1}
                value={duration}
                onChange={e => setDuration(e.target.value)}
                autoFocus
                className="w-full rounded-md border border-border bg-bg-elevated px-2 py-1.5 text-sm focus:outline-none focus:ring-2 focus:ring-accent/30 focus:border-accent"
              />
            </label>
            <label className="block">
              <span className="block text-[10px] uppercase tracking-wider text-fg-subtle font-medium mb-1">
                Date
              </span>
              <input
                type="date"
                value={date}
                onChange={e => setDate(e.target.value)}
                className="w-full rounded-md border border-border bg-bg-elevated px-2 py-1.5 text-sm focus:outline-none focus:ring-2 focus:ring-accent/30 focus:border-accent"
                placeholder="today"
              />
            </label>
          </div>
          <label className="block">
            <span className="block text-[10px] uppercase tracking-wider text-fg-subtle font-medium mb-1">
              Notes
            </span>
            <textarea
              value={notes}
              onChange={e => setNotes(e.target.value)}
              rows={2}
              placeholder="What did you work on?"
              className="w-full rounded-md border border-border bg-bg-elevated px-2 py-1.5 text-sm resize-y focus:outline-none focus:ring-2 focus:ring-accent/30 focus:border-accent"
            />
          </label>
          {error && (
            <div className="rounded-md border border-danger/40 bg-danger/10 px-2.5 py-1.5 text-xs text-danger">
              {error}
            </div>
          )}
          <div className="flex justify-end gap-2 pt-1">
            <button
              type="button"
              onClick={() => { setOpen(false); setError(null) }}
              className="text-xs text-fg-subtle hover:text-fg px-2"
            >
              Cancel
            </button>
            <Button type="submit" size="sm" disabled={logMut.isPending}>
              {logMut.isPending ? 'Logging…' : 'Log to dkbooks'}
            </Button>
          </div>
        </form>
      )}

      {!s.configured && (
        <p className="text-[10px] text-fg-subtle italic mt-2">
          Configure project's dkbooks_project_id (Settings) to enable time tracking on this story.
        </p>
      )}
    </Section>
  )
}

function Section({ children }: { children: React.ReactNode }) {
  return (
    <Card padded className="mt-4">
      <h3 className="text-xs font-semibold uppercase tracking-wider text-accent mb-3 flex items-center gap-1.5">
        <Clock size={12} /> Time · dkbooks
      </h3>
      {children}
    </Card>
  )
}

function Stat({ label, value, sub, icon }: { label: string; value: string; sub?: string; icon?: React.ReactNode }) {
  return (
    <div className="rounded-md border border-border bg-bg-elevated px-2 py-1.5">
      <div className="flex items-center justify-between text-[10px] uppercase tracking-wider text-fg-subtle">
        <span>{label}</span>
        {icon}
      </div>
      <div className="text-base font-semibold tabular-nums">{value}</div>
      {sub && <div className="text-[10px] text-fg-subtle font-mono">{sub}</div>}
    </div>
  )
}

function fmtMins(m: number): string {
  const h = Math.floor(m / 60)
  const min = m % 60
  if (h === 0) return `${min}m`
  if (min === 0) return `${h}h`
  return `${h}h${min}m`
}
